import React, { useEffect, useState } from 'react';
import { X, RotateCcw, Trash2, Clock } from 'lucide-react';
import ConfirmationModal from './ConfirmationModal';

const timeAgo = (timestamp) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " years ago";

    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " months ago";

    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " days ago";

    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " hours ago";

    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " mins ago";

    return "Just now";
};

const HistoryDrawer = ({
    isOpen,
    onClose,
    history,
    onRestore,
    onDelete,
    onClearHistory
}) => {
    const [confirmAction, setConfirmAction] = useState({ type: null, id: null });

    // Prevent body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    const handleConfirm = () => {
        if (confirmAction.type === 'restore') {
            onRestore(confirmAction.id);
        } else if (confirmAction.type === 'clear') {
            onClearHistory();
        }
        setConfirmAction({ type: null, id: null });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] flex justify-end">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            ></div>

            {/* Drawer */}
            <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col transform transition-transform animate-slide-in-right">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-slate-50">
                    <div className="flex items-center gap-2 text-slate-800">
                        <Clock className="w-5 h-5 text-slate-500" />
                        <h2 className="text-lg font-bold">Version History</h2>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-xs font-semibold text-slate-400 bg-slate-200 px-2 py-1 rounded-full">
                            {history.length}/30
                        </span>
                        <button
                            onClick={onClose}
                            className="p-1 hover:bg-slate-200 rounded-full transition-colors text-slate-500"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50">
                    {history.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                            <Clock size={48} className="mb-2 opacity-20" />
                            <p className="text-sm">No history snapshots yet.</p>
                            <p className="text-xs text-slate-300 mt-1">Snapshots are created when you share.</p>
                        </div>
                    ) : (
                        history.map((snapshot) => (
                            <div
                                key={snapshot.id}
                                className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow group flex flex-col gap-3"
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="font-semibold text-slate-800 text-sm">{snapshot.label}</p>
                                        <p className="text-xs text-slate-400 mt-0.5">{timeAgo(snapshot.timestamp)}</p>
                                    </div>
                                    <button
                                        onClick={() => onDelete(snapshot.id)}
                                        className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                        title="Delete Snapshot"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>

                                <div className="pt-2 border-t border-slate-50 flex justify-end">
                                    <button
                                        onClick={() => setConfirmAction({ type: 'restore', id: snapshot.id })}
                                        className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
                                    >
                                        <RotateCcw size={14} />
                                        Restore
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                {history.length > 0 && (
                    <div className="p-4 border-t border-gray-100 bg-white">
                        <button
                            onClick={() => setConfirmAction({ type: 'clear' })}
                            className="w-full py-2 text-red-500 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
                        >
                            Clear All History
                        </button>
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={!!confirmAction.type}
                onClose={() => setConfirmAction({ type: null, id: null })}
                onConfirm={handleConfirm}
                title={confirmAction.type === 'restore' ? "Restore Version?" : "Clear History?"}
                message={confirmAction.type === 'restore'
                    ? "Are you sure you want to restore this version? Current unsaved changes will be lost."
                    : "Are you sure you want to delete all history? This action cannot be undone."}
                confirmText={confirmAction.type === 'restore' ? "Yes, Restore" : "Yes, Clear All"}
            />
        </div>
    );
};

export default HistoryDrawer;
